import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import messagingService from "../../services/messagingService";
import {
  FiMapPin,
  FiGlobe,
  FiExternalLink,
  FiTwitter,
  FiGithub,
  FiLinkedin,
  FiCheck,
  FiUserPlus,
  FiMessageCircle,
  FiUsers,
  FiEdit3,
  FiStar,
  FiAward,
  FiBookOpen,
  FiTarget,
  FiMoreHorizontal,
  FiShare2,
} from "react-icons/fi";
import "./ProfileHeader.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const buttonVariants = {
  hover: { scale: 1.02, transition: { duration: 0.2 } },
  tap: { scale: 0.98 },
};

const ProfileHeader = ({
  profile,
  isOwnProfile,
  isFollowing,
  handleFollow,
  handleCollaborate,
  stats,
  openFollowersModal,
  openFollowingModal,
  openCollaboratorsModal,
}) => {
  const navigate = useNavigate();
  const [showAllLinks, setShowAllLinks] = useState(false);
  const [showAllInterests, setShowAllInterests] = useState(false);
  const [showAllResearchInterests, setShowAllResearchInterests] = useState(false);
  const [messageSending, setMessageSending] = useState(false);

  const handleMessage = async () => {
    const targetUserId = profile?._id || profile?.id;
    if (!targetUserId || messageSending) return;
    setMessageSending(true);
    try {
      const response = await messagingService.startConversation(targetUserId);
      if (response.success) {
        navigate("/messages", {
          state: {
            selectedConversation: response.data.conversation,
            isNew: response.data.isNew,
          },
        });
      }
    } catch (error) {
      if (error.message?.includes("follow")) {
        toast.error("You must follow or collaborate with this user to send messages");
      } else {
        toast.error(error.message || "Failed to start conversation");
      }
    } finally {
      setMessageSending(false);
    }
  };

  const getIconForLink = (platform) => {
    const platformLower = platform?.toLowerCase() || "";
    if (platformLower.includes("twitter") || platformLower.includes("x"))
      return <FiTwitter />;
    if (platformLower.includes("github")) return <FiGithub />;
    if (platformLower.includes("linkedin")) return <FiLinkedin />;
    return <FiGlobe />;
  };

  const averageRating = profile?.averageRating ?? stats?.averageRating ?? 0;
  const totalRatings = profile?.totalRatings ?? stats?.totalRatings ?? 0;

  // Combine external links
  const allLinks = [
    ...(profile?.externalLinks?.filter((l) => l.isPublic) || []),
    ...(profile?.professionalLinks || []),
  ];
  const visibleLinks = showAllLinks ? allLinks : allLinks.slice(0, 3);

  return (
    <motion.header
      className="prf-header"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="prf-header-content">
        {/* Left Section - Avatar & Quick Stats */}
        <motion.div className="prf-avatar-section" variants={itemVariants}>
          <div className="prf-avatar-wrapper">
            <motion.img
              src={
                profile?.profilePhotoUrl ||
                profile?.profilePhoto ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  profile?.name || "User"
                )}&background=3b82f6&color=ffffff&size=200`
              }
              alt={profile?.name}
              className="prf-avatar"
              whileHover={{ scale: 1.03 }}
              transition={{ duration: 0.3 }}
            />
            {profile?.isVerified && (
              <motion.div
                className="prf-verified-badge"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 500 }}
              >
                <FiCheck />
              </motion.div>
            )}
            {profile?.isOpenForCollaboration && (
              <div
                className="prf-collab-indicator"
                title="Open for collaboration"
              >
                <FiUsers />
              </div>
            )}
          </div>

          {/* Social Stats - Compact Row */}
          <motion.div className="prf-quick-stats" variants={itemVariants}>
            <motion.button
              className="prf-stat-btn"
              onClick={openFollowersModal}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="prf-stat-num">{stats?.followers || 0}</span>
              <span className="prf-stat-label">Followers</span>
            </motion.button>
            <div className="prf-stat-divider" />
            <motion.button
              className="prf-stat-btn"
              onClick={openFollowingModal}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="prf-stat-num">{stats?.following || 0}</span>
              <span className="prf-stat-label">Following</span>
            </motion.button>
            <div className="prf-stat-divider" />
            <motion.button
              className="prf-stat-btn"
              onClick={openCollaboratorsModal}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="prf-stat-num">{stats?.collaborators || 0}</span>
              <span className="prf-stat-label">Collabs</span>
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Center Section - Profile Info */}
        <motion.div className="prf-info-section" variants={itemVariants}>
          {/* Name & Badge */}
          <div className="prf-name-row">
            <h1 className="prf-name">
              {profile?.name || "Professional Name"}
              {profile?.isVerified && (
                <span className="prf-verified-inline" title="Verified">
                  <FiCheck />
                </span>
              )}
            </h1>
            {profile?.professionalRole && (
              <span className="prf-role-tag">{profile.professionalRole}</span>
            )}
          </div>

          {/* Rating Display */}
          {totalRatings > 0 && (
            <motion.div className="prf-rating" variants={itemVariants}>
              <div className="prf-rating-stars">
                {[...Array(5)].map((_, i) => (
                  <FiStar
                    key={i}
                    className={`prf-star ${
                      i < Math.floor(averageRating) ? "filled" : ""
                    }`}
                  />
                ))}
              </div>
              <span className="prf-rating-text">
                {averageRating?.toFixed(1)} ({totalRatings} reviews)
              </span>
            </motion.div>
          )}

          {/* Institution */}
          {(profile?.institution || profile?.education) && (
            <p className="prf-institution">
              <FiAward className="prf-icon" />
              {profile?.institution || profile?.education}
            </p>
          )}

          {/* Field & Interests - Inline Compact with Expand */}
          {profile?.field && (
            <div className="prf-field-row prf-expandable-row">
              <span className="prf-field-badge">
                <FiBookOpen />
                {profile.field}
              </span>
              {(showAllInterests
                ? profile?.interests
                : profile?.interests?.slice(0, 3)
              )?.map((interest, i) => (
                <span key={i} className="prf-interest-tag">
                  {interest}
                </span>
              ))}
              {profile?.interests?.length > 3 && (
                <motion.button
                  className="prf-more-tag prf-more-clickable"
                  onClick={() => setShowAllInterests(!showAllInterests)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  title={showAllInterests ? "Show less" : "Show all interests"}
                >
                  {showAllInterests
                    ? "−"
                    : `+${profile.interests.length - 3}`}
                </motion.button>
              )}
            </div>
          )}

          {/* Research Focus - with Expand */}
          {profile?.researchInterests?.length > 0 && (
            <div className="prf-research-row prf-expandable-row">
              <FiTarget className="prf-research-icon" />
              <div className="prf-research-tags">
                {(showAllResearchInterests
                  ? profile.researchInterests
                  : profile.researchInterests.slice(0, 4)
                ).map((interest, i) => (
                  <span key={i} className="prf-research-tag">
                    {interest}
                  </span>
                ))}
                {profile.researchInterests.length > 4 && (
                  <motion.button
                    className="prf-more-tag prf-more-clickable"
                    onClick={() =>
                      setShowAllResearchInterests(!showAllResearchInterests)
                    }
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    title={
                      showAllResearchInterests
                        ? "Show less"
                        : "Show all research interests"
                    }
                  >
                    {showAllResearchInterests
                      ? "−"
                      : `+${profile.researchInterests.length - 4}`}
                  </motion.button>
                )}
              </div>
            </div>
          )}

          {/* Contact Info - Compact Inline */}
          <div className="prf-contact-row">
            {profile?.location && (
              <span className="prf-contact-item">
                <FiMapPin />
                {profile.location}
              </span>
            )}
            {profile?.website && (
              <a
                href={profile.website}
                target="_blank"
                rel="noopener noreferrer"
                className="prf-contact-item prf-link"
              >
                <FiGlobe />
                Website
                <FiExternalLink className="prf-external" />
              </a>
            )}
          </div>

          {/* External Links */}
          {allLinks.length > 0 && (
            <motion.div className="prf-links-row" variants={itemVariants}>
              {visibleLinks.map((link, i) => (
                <motion.a
                  key={i}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="prf-link-chip"
                  whileHover={{ y: -2, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {getIconForLink(link.platform)}
                  <span>{link.platform || "Link"}</span>
                </motion.a>
              ))}
              {allLinks.length > 3 && !showAllLinks && (
                <motion.button
                  className="prf-more-links"
                  onClick={() => setShowAllLinks(true)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FiMoreHorizontal />
                  <span>+{allLinks.length - 3}</span>
                </motion.button>
              )}
            </motion.div>
          )}
        </motion.div>

        {/* Right Section - Actions */}
        <motion.div className="prf-actions-section" variants={itemVariants}>
          {!isOwnProfile ? (
            <>
              <motion.button
                className={`prf-btn prf-btn-primary ${
                  isFollowing ? "following" : ""
                }`}
                onClick={() => handleFollow()}
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <FiUserPlus />
                <span>{isFollowing ? "Following" : "Follow"}</span>
              </motion.button>
              <motion.button
                className="prf-btn prf-btn-secondary"
                onClick={handleMessage}
                disabled={messageSending}
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <FiMessageCircle />
                <span>{messageSending ? "Opening..." : "Message"}</span>
              </motion.button>
              <motion.button
                className="prf-btn prf-btn-outline"
                onClick={handleCollaborate}
                disabled={!profile?.isOpenForCollaboration}
                title={
                  profile?.isOpenForCollaboration
                    ? "Send collaboration request"
                    : "Not available for collaboration"
                }
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <FiUsers />
                <span>Collaborate</span>
              </motion.button>
            </>
          ) : (
            <>
              <motion.div
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <Link
                  to="/settings/profile"
                  className="prf-btn prf-btn-primary"
                >
                  <FiEdit3 />
                  <span>Edit Profile</span>
                </Link>
              </motion.div>
              <motion.button
                className="prf-btn prf-btn-ghost"
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <FiShare2 />
                <span>Share</span>
              </motion.button>
            </>
          )}
        </motion.div>
      </div>
    </motion.header>
  );
};

export default ProfileHeader;
